import { HttpWebtrack } from 'utility/http';

import { store } from '@redux/storeConfig/store';
import * as alerts from '@redux/utils/alerts';
import { ConfigActions } from 'utility/http/http-events';
import configureInterceptors from 'utility/http/interceptors/configure-interceptors';
import { appIntl } from '../../../../utility/context/IntlGlobalProvider';

export const baseUrl = '/route';

interface Request {
  routeAssignmentId: number;
}

interface SharedLinkRoutesRequest {
  sharedLinkId: number;
  token: string;
}

export const getRouteExecutionsReportService = async (
  request: any,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getExecutionsReport`, request);
};

export const getRouteExecutionService = async (request: Request, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getExecution`, request);
};

export const getRouteExecutionsMonitorService = async (
  request: any,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getExecutions`, request);
};

export const getRouteReportService = async (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getReport`, request);
};

export const saveRouteExecutionService = (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: `${request.unitName}`,
    object: 'routes.assignmentOfUnit',
    action: request.routeAssignmentId ? 'Edit' : 'Create',
    genderType: 'SingularFemale'
  });
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/saveExecution`, request);
};

export const changeToAlternateRouteService = (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/changeToAlternateRoute`, request);
};

export const shareRouteExecutionService = (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.shareRoute',
    object: 'routes.shareRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/shareRouteExecutions`, request);
};

export const manualTransitRouteExecutionService = (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.manualTransit',
    object: 'routes.manualTransit',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, request);
};

export const startRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.startRoute',
    object: 'routes.startRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 3;
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasStarted'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesStartMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesStartMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const pauseRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.pauseRoute',
    object: 'routes.pauseRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 5;
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasPaused'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesPauseMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesPauseMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const finalizeRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.finalizeRoute',
    object: 'routes.finalizeRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 6;
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasFinished'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesFinalizationMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesFinalizationMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const completeRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.completeRoute',
    object: 'routes.completeRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 2;
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasCompleted'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesCompletationMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesCompletationMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const cancelRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.cancelRoute',
    object: 'routes.cancelRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 8;
  console.log('routeData', request);
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  console.log('completeRequest', routes);
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasCanceled'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesCancelationMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesCancelationMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const removeRouteExecutionService = (request: any, setIsExecutingAction: any) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: 'routes.removeRoute',
    object: 'routes.removeRoute',
    action: 'Edit',
    genderType: 'SingularFemale'
  });
  setIsExecutingAction(true);
  const newStatus = 0;
  const routes = JSON.stringify(request.map((route) => ({ ...route, newRouteStatus: newStatus })));
  const configActions = {
    successAction: (response) => {
      if (response.success) {
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'routes.selectedRoutesWasRemoved'
          })
        });
        setIsExecutingAction(false);
      } else {
        const error = {
          errorMessage: appIntl().formatMessage({
            id: 'routes.routesRemoveMessageError'
          }),
          errorNumber: -1
        };
        alerts.showErrorAlertFailChanges({ error });
        setIsExecutingAction(false);
      }
    },
    errorAction: (err) => {
      const error = {
        errorMessage: appIntl().formatMessage({
          id: 'routes.routesRemoveMessageError'
        }),
        errorNumber: -1
      };
      alerts.showErrorAlertFailChanges({ error });
      setIsExecutingAction(false);
    }
  };
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateRouteExecutionStatus`, routes);
};

export const getSharedLinkRoutesService = async (
  request: SharedLinkRoutesRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getSharedLinkRoutes`, request);
};

export const bulkUpdateRouteExecutionsService = (request: any, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigMessage({
    name: `${request.routeAssignmentIds?.length || 0} ${appIntl().formatMessage({
      id: 'routes.routes'
    })}`,
    object: 'routes.routes',
    action: 'Edit',
    genderType: 'PluralFemale'
  });
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/bulkUpdateExecutions`, request);
};
